import { RemoveCircle, Warning } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Chip, IconButton, Stack, Theme, Tooltip, Typography } from "@mui/material";
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridActionsColDef,
  GridColTypeDef,
  GridColumns,
  gridDetailPanelExpandedRowsContentCacheSelector,
  GridRenderCellParams,
  useGridApiContext,
  useGridSelector,
  ValueOptions,
} from "@mui/x-data-grid-pro";
import { dateColDef } from "components/DataGrid/columnDefs/dateColDef";
import { dateTimeColDef } from "components/DataGrid/columnDefs/dateTimeColDef";
import { singleSelectColDef } from "components/DataGrid/columnDefs/singleSelectColDef";
import { CustomerReturnActions } from "components/Fulfillment/CustomerReturnActions";
import { GenerateReplacementRowAction } from "components/Fulfillment/GenerateReplacementRowAction";
import { ManualReplacementRowAction } from "components/Fulfillment/ManualReplacementRowAction";
import { MarkAsHiddenRowAction } from "components/Fulfillment/MarkAsHiddenRowAction";
import { MarkAsHighPriorityRowAction } from "components/Fulfillment/MarkAsHighPriorityRowAction";
import { MarkAsPackedRowAction } from "components/Fulfillment/MarkAsPackedRowAction";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import { useAlert } from "hooks/useAlert";
import { useConfirmation } from "hooks/useConfirmation";
import { isValidElement } from "react";
import { Link } from "react-router-dom";
import { api } from "services/api.service";
import { BulkOrder } from "types/bulkOrder";
import { Inventory } from "types/inventory";
import { ShipstationOrderItem } from "types/shipstationOrderItem";
import { humanize, pluralize } from "utils/helpers";
import { getShipByLabel } from "utils/shipBy";
import { warehouseColDef } from "../DataGrid/columnDefs/warehouseColDef";

function getEmojiForState(orderItem: ShipstationOrderItem) {
  switch (orderItem.state.toLowerCase()) {
    case "created":
      if (orderItem.inventoryItem === null) return "🔍";
      if (orderItem.addressVerifiedAt === null) return "⚠️";
      if (orderItem.lastAssignedUser === null) return "🕕";
      return "👷";
    case "skipped":
      return "🟡️";
    case "packed":
      return "📦";
    case "on_cart":
      return "🛒";
    case "cancelled":
      return "⛔️";
    default:
      return "";
  }
}

export const subStatusCalculation = (row: ShipstationOrderItem) => {
  if (row.fulfillmentType === "CustomerReturnItem") {
    if (row.returnStatus === "returned") return "Returned";
    if (["cancelled", "skipped"].includes(row.state)) return "Return pending — Ops resolution required";
    if (row.state === "packed") return "Return pending — awaiting delivery";
  }
  const hasInventory = row.inventoryItem !== null;
  const isPacked = row.state === "packed";
  const isOpen = row.state === "created";

  if (isOpen) {
    return !hasInventory
      ? "Looking for Inventory"
      : row.addressVerifiedAt === null
        ? "Address issue"
        : row.lastAssignedUser !== null
          ? "Fulfillment Started"
          : "Awaiting Batch";
  }

  if (isPacked && row.shipByDate && row.packedAt) {
    if (row.packedAt <= row.shipByDate) {
      return "On Time";
    } else {
      return "Late";
    }
  }

  return null;
};

const itemStateColDef: GridColTypeDef = {
  renderCell: (params) => {
    const color = params.value === "No inventory assigned" ? "gray" : "default";
    const isError = params.value === "Skipped";
    const isPacked = params.value === "Packed";
    const isManuallyPacked = isPacked && params.row.packedReason;

    return (
      <Stack color={color} direction={"row"} gap={1}>
        <Typography>{getEmojiForState(params.row)}</Typography>
        {params.value} {subStatusCalculation(params.row)}
        {isError && <Typography color="error">{params.row.failureReason}</Typography>}
        {isManuallyPacked && (
          <Tooltip title={`Packed by: ${params.row.packedBy}`} placement="top" disableInteractive={true}>
            <Typography color="gray" sx={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
              {params.row.packedReason}
            </Typography>
          </Tooltip>
        )}
      </Stack>
    );
  },
  sortable: false,
};

const inventoryItemColDef: GridColTypeDef = {
  sortable: false,
  renderCell: (params) => {
    return <Link to={`/inventories/${params.value}`}>{params.value}</Link>;
  },
};
const ageColDef: GridColTypeDef = {
  sortable: true,
  filterable: false,
  renderCell: (params) => {
    if (!params.row.addressVerifiedAt) return null;
    const addressVerifiedAt = new Date(params.row.addressVerifiedAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - addressVerifiedAt.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
    if (diffHours < 48) return <Typography>{pluralize(diffHours, "hour", "hours")}</Typography>;
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));
    if (diffMinutes < 60) return <Typography>{pluralize(diffMinutes, "minute", "minutes")}</Typography>;

    return <Typography>{diffDays} days</Typography>;
  },
};
const shipByDef: GridColTypeDef = {
  ...dateTimeColDef,
  sortable: true,
  filterable: true,
  renderCell: (params) => {
    if (!params.row.shipByDate) {
      return "-";
    }

    return (
      <Tooltip title={new Date(params.row.shipByDate).toString()}>
        <Typography>
          {getShipByLabel(params.row.shipByDate, params.row.warehouseTimezone, params.row.state) || "-"}
        </Typography>
      </Tooltip>
    );
  },
};
const actionColDef: GridActionsColDef<ShipstationOrderItem> = {
  field: "actions",
  sortable: false,
  pinnable: true,
  type: "actions",
  align: "right",
  getActions: (params) =>
    params.row.fulfillmentType === "CustomerReturnItem"
      ? [<CustomerReturnActions key="return" row={params.row} />]
      : [
          <GenerateReplacementRowAction row={params.row} />,
          <ManualReplacementRowAction row={params.row} />,
          <MarkAsPackedRowAction row={params.row} showInMenu={true} />,
          <MarkAsHighPriorityRowAction row={params.row} showInMenu={true} />,
          <MarkAsHiddenRowAction row={params.row} showInMenu={true} />,
        ],
};
const addressVerifiedAtColDef: GridColTypeDef = {
  ...dateColDef,
  renderCell: (params) => {
    if (!params.value)
      return (
        <Tooltip title={params.row.addressVerification} placement="top">
          <Stack alignItems="center" direction="row" gap={1}>
            Not Validated <Warning color="warning" fontSize="small" />
          </Stack>
        </Tooltip>
      );
    return dateColDef.renderCell?.(params);
  },
};
const stateValueOptions: ValueOptions[] = [
  { value: "created", label: "Open" },
  { value: "skipped", label: "Skipped" },
  { value: "packed", label: "Packed" },
  { value: "on_cart", label: "On Cart" },
  { value: "cancelled", label: "Cancelled" },
];

export const CustomDetailPanelToggle = (props: Pick<GridRenderCellParams, "id" | "value">) => {
  const { id, value: isExpanded } = props;
  const apiRef = useGridApiContext();
  const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector);
  const hasDetail = isValidElement(contentCache[id]);

  return (
    <IconButton size="small" tabIndex={-1} disabled={!hasDetail}>
      <ExpandMoreIcon
        sx={{
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: (theme: Theme) =>
            theme.transitions.create("transform", {
              duration: theme.transitions.duration.shortest,
            }),
        }}
        fontSize="inherit"
      />
    </IconButton>
  );
};

type InventoryRow = Inventory & { rowNumber: number };

const RemoveBulkOrderInventoryCell = ({ row }: { row: InventoryRow }) => {
  const { confirm } = useConfirmation();
  const { alertSuccess, alertError } = useAlert();

  return (
    <IconButton
      color="error"
      size="small"
      onClick={() =>
        confirm({
          handler: () =>
            api
              .delete(`/bulk_orders/inventories/${row.id}`)
              .then(() => alertSuccess("Inventory removed from bulk order successfully"))
              .catch((error: { statusText: string }) =>
                alertError(`Failed to delete bulk order: Status ${error.statusText}`),
              ),
          message: `Are you sure you want to remove Inventory ${row.id} (${row.label}) from the bulk order?`,
        })
      }
    >
      <RemoveCircle />
    </IconButton>
  );
};

export const columns = [
  { field: "id", headerName: "ID", width: 100 },
  { field: "age", headerName: "Age", width: 100, ...ageColDef },
  { field: "shipBy", headerName: "Ship By", width: 100, ...shipByDef },
  { field: "deviceType", headerName: "Device Type", width: 150 },
  { field: "orderNumber", headerName: "Order Number", width: 150 },
  { field: "state", headerName: "State", width: 300, ...singleSelectColDef(stateValueOptions), ...itemStateColDef },
  { field: "addressVerifiedAt", headerName: "Address Validation", width: 150, ...addressVerifiedAtColDef },
  { field: "orderedAt", headerName: "Order Date", width: 150, ...dateColDef },
  { field: "sku", headerName: "Order SKU", width: 150, sortable: false },
  { field: "channelName", headerName: "Channel", width: 150 },
  { field: "lastAssignedUser", headerName: "Last User Assigned", width: 200 },
  { field: "warehouse", headerName: "Warehouse", ...warehouseColDef },
  { field: "inventoryItem", headerName: "Inventory Item", width: 150, ...inventoryItemColDef },
  { headerName: "Actions", width: 140, ...actionColDef },
];

const bulkOrdersStateValueOptions: ValueOptions[] = [
  { value: "unpacked", label: "Unpacked" },
  { value: "packed", label: "Ready for invoice" },
  { value: "completed", label: "Completed" },
];

const bulkOrdersStateOptions = {
  unpacked: "#E0E0E0",
  packed: "skyblue",
  completed: "#4CAF5014",
};

export const bulkOrdersColumns = [
  { field: "customerName", headerName: "Customer Name", width: 150 },
  { field: "manifestId", headerName: "Manifest ID", width: 150 },
  { field: "unitCount", headerName: "Unit Count", width: 100 },
  { field: "warehouseCode", headerName: "Warehouse Code", width: 100 },
  { field: "shippingMethod", headerName: "Shipping Method", width: 100 },
  { field: "dueDate", headerName: "Due Date", width: 100, ...dateColDef },
  {
    field: "state",
    headerName: "State",
    width: 150,
    ...singleSelectColDef(bulkOrdersStateValueOptions),
    renderCell: (params: GridRenderCellParams<BulkOrder>) => {
      return (
        <Chip
          sx={{ backgroundColor: bulkOrdersStateOptions[params.row.state as keyof typeof bulkOrdersStateOptions] }}
          label={humanize(params.row.state).replace(/ /g, "_")}
        />
      );
    },
  },
  { field: "type", headerName: "Type", width: 150 },
  {
    ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    renderCell: (params: GridRenderCellParams<BulkOrder>) => <CustomDetailPanelToggle {...params} />,
    filterable: false,
    hideable: false,
    headerName: "Details",
    type: "string",
  },
];

export const inventoriesColumns: GridColumns<InventoryRow> = [
  { field: "rowNumber", headerName: "No.", width: 50 },
  { field: "id", headerName: "Inventory ID", width: 150 },
  { field: "sku", headerName: "SKU", width: 150 },
  { field: "label", headerName: "Label", width: 150 },
  {
    field: "warehouseLocation",
    headerName: "Location",
    width: 250,
    renderCell: ({ row }: GridRenderCellParams<InventoryRow>) => (
      <WarehouseLocationLink code={row.warehouseLocation} locationId={row.locationId} />
    ),
  },
  { field: "model", headerName: "Model", width: 200 },
  { field: "serialNumber", headerName: "Serial Number", width: 200 },
  { field: "condition", headerName: "Condition", width: 100 },
  {
    field: "actions",
    headerName: "Remove",
    width: 60,
    sortable: false,
    filterable: false,
    hideable: false,
    renderCell: (params: GridRenderCellParams<InventoryRow>) => <RemoveBulkOrderInventoryCell row={params.row} />,
  },
];
